import { safeQuerySelectorAll, safeAddEventListener } from './utils';

interface TextOptions {
  preventSelection?: boolean;
  preventCopy?: boolean;
  preventPrintScreen?: boolean;
  targetSelector?: string;
  observeDynamicContent?: boolean; // Add option to enable/disable MutationObserver
}

class TextShield {
  private options: TextOptions;
  private eventListeners: { element: HTMLElement | Document; type: string; handler: EventListener }[] = [];
  private styleElement: HTMLStyleElement | null = null;
  private mutationObserver: MutationObserver | null = null;

  constructor(options: TextOptions = {}) {
    this.options = {
      preventSelection: true,
      preventCopy: true,
      preventPrintScreen: false,
      targetSelector: 'p, h1, h2, h3, h4, h5, h6, span, div',
      observeDynamicContent: true, // Default to true
      ...options
    };
    
    this.initialize();
  }

  private initialize(): void {
    try {
      if (typeof document === 'undefined') return; // Skip for SSR
      
      if (this.options.preventSelection) {
        this.applySelectionProtection();
      }
      
      if (this.options.preventCopy) {
        this.applyCopyProtection();
      }
      
      if (this.options.preventPrintScreen) {
        this.applyPrintScreenProtection();
      }
      
      // Observe DOM changes to protect new content
      if (this.options.observeDynamicContent) {
        this.setupMutationObserver();
      }
    } catch (error) {
      console.error('web-shield: Error initializing TextShield', error);
    }
  }

  private applySelectionProtection(): void {
    try {
      this.styleElement = document.createElement('style');
      this.styleElement.innerHTML = `
        .web-shield-text {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
      `;
      document.head.appendChild(this.styleElement);
      
      // Use targetSelector if provided
      const selector = this.options.targetSelector || 'p, h1, h2, h3, h4, h5, h6, span, div';
      this.protectTextElements(selector);
      
      // Instead of applying to document, use event delegation with element checking
      const selectionHandler = (e: Event) => {
        // Only prevent selection if the target matches our selector
        if (e.target instanceof Element) {
          // Check if the event target or any of its parents match our selector
          let currentElement: Element | null = e.target;
          while (currentElement) {
            if (currentElement.matches && currentElement.matches(selector)) {
              e.preventDefault();
              return false;
            }
            currentElement = currentElement.parentElement;
          }
        }
      };
      
      // We still need to listen at document level, but we'll filter targets
      safeAddEventListener(document, 'selectstart', selectionHandler);
      this.eventListeners.push(
        { element: document, type: 'selectstart', handler: selectionHandler }
      );
    } catch (error) {
      console.error('web-shield: Error applying selection protection', error);
    }
  }
  
  private protectTextElements(selector: string): void {
    try {
      safeQuerySelectorAll(selector).forEach(el => {
        el.classList.add('web-shield-text');
      });
    } catch (error) {
      console.error('web-shield: Error protecting text elements', error);
    }
  }

  private applyCopyProtection(): void {
    try {
      const selector = this.options.targetSelector || 'p, h1, h2, h3, h4, h5, h6, span, div';
      
      const copyHandler = (e: Event) => {
        // Only prevent copy if selection contains protected elements
        if (window.getSelection) {
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            
            // Create a temporary element to check what was selected
            const tempElement = document.createElement('div');
            tempElement.appendChild(range.cloneContents());
            
            // Check if any selected content matches our selector or is inside an element matching our selector
            const hasProtectedContent = 
              // Check if any direct children match
              tempElement.querySelectorAll(selector).length > 0 ||
              // Check if any parent of selected content matches
              (e.target instanceof Element && (function(elem: Element) {
                let current: Element | null = elem;
                while (current) {
                  if (current.matches && current.matches(selector)) return true;
                  current = current.parentElement;
                }
                return false;
              })(e.target));
              
            if (hasProtectedContent) {
              e.preventDefault();
              return false;
            }
          }
        }
      };
      
      safeAddEventListener(document, 'copy', copyHandler);
      safeAddEventListener(document, 'cut', copyHandler);
      
      this.eventListeners.push(
        { element: document, type: 'copy', handler: copyHandler },
        { element: document, type: 'cut', handler: copyHandler }
      );
    } catch (error) {
      console.error('web-shield: Error applying copy protection', error);
    }
  }

  private applyPrintScreenProtection(): void {
    try {
      const keydownHandler = (e: Event) => {
        try {
          const keyEvent = e as KeyboardEvent;
          if (keyEvent.key === 'PrintScreen' || 
             (keyEvent.ctrlKey && keyEvent.key === 'p') || 
             (keyEvent.ctrlKey && keyEvent.shiftKey && keyEvent.key === 'I')) {
            e.preventDefault();
            return false;
          }
        } catch (error) {
          console.error('web-shield: Error in keydown handler', error);
        }
      };
      
      safeAddEventListener(document, 'keydown', keydownHandler);
      this.eventListeners.push(
        { element: document, type: 'keydown', handler: keydownHandler }
      );
    } catch (error) {
      console.error('web-shield: Error applying print screen protection', error);
    }
  }

  private setupMutationObserver(): void {
    try {
      if (!window.MutationObserver) return; // Skip if not supported
      
      const selector = this.options.targetSelector || 'p, h1, h2, h3, h4, h5, h6, span, div';
      
      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // Check if any added nodes match our selector
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                // Check if the node itself matches
                if ((node as Element).matches && (node as Element).matches(selector)) {
                  (node as Element).classList.add('web-shield-text');
                }
                
                // Check children that match
                safeQuerySelectorAll(selector, node as Element).forEach(el => {
                  el.classList.add('web-shield-text');
                });
              }
            });
          }
        });
      });

      // Start observing
      this.mutationObserver.observe(document.body, { 
        childList: true,
        subtree: true
      });
    } catch (error) {
      console.error('web-shield: Error setting up mutation observer', error);
    }
  }

  disable(): void {
    try {
      if (this.styleElement) {
        this.styleElement.remove();
      }
      
      safeQuerySelectorAll('.web-shield-text').forEach(el => {
        el.classList.remove('web-shield-text');
      });
      
      this.eventListeners.forEach(({ element, type, handler }) => {
        try {
          element.removeEventListener(type, handler);
        } catch (error) {
          console.warn(`web-shield: Error removing ${type} event listener`, error);
        }
      });
      this.eventListeners = [];
      
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }
    } catch (error) {
      console.error('web-shield: Error disabling TextShield', error);
    }
  }
}

export default TextShield;